"use client";

// ============================================
// MAIN CLIENT RECORDS COMPONENT
// ============================================
import { useState, useEffect, useCallback } from "react";
import {
  getDatabase,
  ref as dbRef,
  onValue,
  get,
  set,
  remove,
} from "firebase/database";
import { db, ref } from "../../Firebase/firestore";
import { push } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
  deleteObject,
  getMetadata,
} from "firebase/storage";
import {
  FaUser,
  FaFileUpload,
  FaFileDownload,
  FaDatabase,
  FaHistory,
  FaSpinner,
  FaFirstOrder,
  FaBoxOpen,
  FaTractor,
  FaTruckLoading,
  FaTruck,
} from "react-icons/fa";

// Section Components
import ProfileSection from "./profiles";
import UploadsSection from "./uploads";
import DocumentsSection from "./documents";
import CodeDetailsSection from "./codes";
import OrderHistorySection from "./orderHistory";
import PurchaseOrderSection from "./purchaseOrders";
import ManualOrderSection from "./manual-order";

// Shared utilities
import {
  INITIAL_CODE_FORM,
  INITIAL_VALIDATION,
  GIF_URL,
  getIndianTimestamp,
  formatTimestamp,
  getMissingDocuments,
  validateCodeForm,
} from "./shared";

import "./clientRecords.css";

// ============================================
// TAB CONFIGURATION
// ============================================
const TABS = [
  { id: "profile", label: "Profile", icon: FaUser },
  { id: "uploads", label: "Upload Files", icon: FaFileUpload },
  { id: "documents", label: "User Documents", icon: FaFileDownload },
  { id: "codeDetails", label: "Code Details", icon: FaDatabase },
  { id: "manualOrder", label: "Manual Order", icon: FaTruck },
  { id: "purchaseOrders", label: "Purchase Orders", icon: FaFileUpload },
  { id: "orderHistory", label: "Order History", icon: FaHistory },
];

// ============================================
// MAIN COMPONENT
// ============================================
const ClientRecords = () => {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  // User & Selection States
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("profile");

  // Loading & Error States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [docsLoading, setDocsLoading] = useState(false);

  // File States
  const [mtcFiles, setMtcFiles] = useState([]);
  const [statementFiles, setStatementFiles] = useState([]);
  const [formatsFiles, setFormatsFiles] = useState([]);
  const [invoicesFiles, setInvoicesFiles] = useState([]);
  const [userDocuments, setUserDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // Code States
  const [userCodes, setUserCodes] = useState([]);
  const [expandedCodes, setExpandedCodes] = useState({});
  const [editingCode, setEditingCode] = useState(null);
  const [editedCodeData, setEditedCodeData] = useState(null);
  const [addingSiteToCode, setAddingSiteToCode] = useState(null);
  const [newSiteForm, setNewSiteForm] = useState({
    address: "",
    state: "",
    pincode: "",
  });
  const [newCodeForm, setNewCodeForm] = useState(INITIAL_CODE_FORM);
  const [validation, setValidation] = useState(INITIAL_VALIDATION);
  const [depots, setDepots] = useState([]);
  // Rate Request States
  const [userRateRequests, setUserRateRequests] = useState([]);

  // Order History States
  const [orderHistory, setOrderHistory] = useState([]);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Address States
  const [copiedAddresses, setCopiedAddresses] = useState([]);
  const [showAddressDropdown, setShowAddressDropdown] = useState(false);
  const [showNewSiteAddressDropdown, setShowNewSiteAddressDropdown] =
    useState(false);

  // Firebase instances
  const storage = getStorage();
  const database = getDatabase();

  // ==========================================
  // NOTIFICATION FUNCTIONS
  // ==========================================

  const sendNotification = async (userId, codeId, title, message) => {
    const notificationRef = ref(
      db,
      `user_notifications/New Code/${userId}/${codeId}`,
    );
    const newNotification = {
      title,
      message,
      gifUrl: GIF_URL,
      timestamp: getIndianTimestamp(),
      seen: false,
    };

    try {
      await set(notificationRef, newNotification);
      console.log("Notification sent successfully:", newNotification);
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const sendMissingDocNotification = (user, missingDocs, codeId = null) => {
    const userId = user.id || user.uid || user.userId || user.email;
    if (!userId) {
      console.error("No valid user ID found for notification", user);
      return;
    }

    const timestamp = new Date();
    const notificationId = `notification_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    const formattedTimestamp = formatTimestamp(timestamp);

    let message = "";
    if (missingDocs.length === 0) {
      message =
        codeId && codeId !== "global"
          ? `All required documents are already uploaded for Code ID: ${codeId}. ✅`
          : `All global documents are already uploaded. ✅`;
    } else {
      message =
        codeId && codeId !== "global"
          ? `Please upload the required documents for Code ID: ${codeId} before month-end closing:\n- ${missingDocs.join("\n- ")}`
          : `Please upload the following documents before month-end closing:\n- ${missingDocs.join("\n- ")}`;
    }

    const notification = {
      title: "Missing Documents Reminder",
      message,
      gifUrl: GIF_URL,
      orderId: null,
      timestamp: formattedTimestamp,
      seen: false,
      clicked: false,
    };

    set(
      ref(db, `user_notifications/Missing_Docs/${userId}/${notificationId}`),
      notification,
    )
      .then(() =>
        console.log("✅ Notification saved:", userId, codeId, missingDocs),
      )
      .catch((err) => console.error("🔥 Error saving notification:", err));
  };

  const fetchUserDocuments = useCallback(
    async (userId) => {
      if (!userId) return;

      setDocsLoading(true);
      try {
        const userDocsRef = dbRef(database, `userDocs/${userId}`);
        const snapshot = await get(userDocsRef);

        if (!snapshot.exists()) {
          setUserDocuments({});
          return;
        }

        const rawData = snapshot.val();
        const documentsByBrand = {};

        // Iterate brands
        for (const brand in rawData) {
          if (!rawData.hasOwnProperty(brand)) continue;
          documentsByBrand[brand] = {};

          const sites = rawData[brand];
          // Iterate site addresses (siteAddress is folder)
          for (const siteAddress in sites) {
            if (!sites.hasOwnProperty(siteAddress)) continue;
            documentsByBrand[brand][siteAddress] = sites[siteAddress]; // paths only
          }
        }

        setUserDocuments(documentsByBrand);
      } catch (err) {
        console.error("Error fetching user documents from RTDB:", err);
        setError(`Error fetching user documents: ${err.message}`);
        setUserDocuments({});
      } finally {
        setDocsLoading(false);
      }
    },
    [database],
  );
  const fetchUserFiles = useCallback(
    async (userId) => {
      try {
        // ================= MTC FILES =================
        const mtcRef = storageRef(storage, `mtc/${userId}`);
        const mtcList = await listAll(mtcRef);

        const mtcUrls = await Promise.all(
          mtcList.items.map(async (file) => {
            const metadata = await getMetadata(file);
            const url = await getDownloadURL(file);

            return {
              name: file.name,
              url,
              createdAt: metadata.timeCreated, // ✅ DATE ADDED
            };
          }),
        );

        // ================= STATEMENT FILES =================
        let statementUrls = [];
        try {
          const statementRef = storageRef(storage, `user-statements/${userId}`);
          const statementList = await listAll(statementRef);

          statementUrls = await Promise.all(
            statementList.items.map(async (file) => {
              const metadata = await getMetadata(file);
              const url = await getDownloadURL(file);

              return {
                name: file.name,
                url,
                createdAt: metadata.timeCreated, // ✅ DATE ADDED
              };
            }),
          );
        } catch {
          // No statements found
        }

        // ================= FORMAT FILES =================

        // ================= INVOICE FILES =================
        let invoicesUrls = [];
        try {
          const invoicesRef = storageRef(storage, `invoices/${userId}`);
          const invoicesList = await listAll(invoicesRef);

          invoicesUrls = await Promise.all(
            invoicesList.items.map(async (file) => {
              const metadata = await getMetadata(file);
              const url = await getDownloadURL(file);

              return {
                name: file.name,
                url,
                createdAt: metadata.timeCreated,
              };
            }),
          );
        } catch {
          // No invoices found
        }
        let formatsUrls = [];
        try {
          const formatsRef = storageRef(storage, `formats/${userId}`);
          const formatsList = await listAll(formatsRef);

          formatsUrls = await Promise.all(
            formatsList.items.map(async (file) => {
              const metadata = await getMetadata(file);
              const url = await getDownloadURL(file);

              return {
                name: file.name,
                url,
                createdAt: metadata.timeCreated, // ✅ DATE ADDED
              };
            }),
          );
        } catch {
          // No formats found
        }

        setMtcFiles(mtcUrls.length > 0 ? mtcUrls.reverse() : []);
        setStatementFiles(
          statementUrls.length > 0 ? statementUrls.reverse() : [],
        );
        setFormatsFiles(formatsUrls.length > 0 ? formatsUrls.reverse() : []);
        setInvoicesFiles(invoicesUrls.length > 0 ? invoicesUrls.reverse() : []);
      } catch (err) {
        setError(`Error fetching files: ${err.message}`);
      }
    },
    [storage],
  );

  const fetchUserCodes = useCallback(
    async (userId) => {
      if (!userId) return;

      try {
        setLoading(true);
        setUserCodes([]);

        const userCodesRef = dbRef(database, `new-code/${userId}/codes`);
        const codesSnapshot = await get(userCodesRef);

        if (codesSnapshot.exists()) {
          const codesData = codesSnapshot.val();
          const codesArray = Object.entries(codesData).map(
            ([codeKey, codeData]) => ({
              id: codeKey,
              ...codeData,
            }),
          );
          setUserCodes(codesArray);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching user codes:", err);
        setError(`Error fetching user codes: ${err.message}`);
        setLoading(false);
      }
    },
    [database],
  );

  const fetchUserRateRequests = useCallback(
    async (userId) => {
      if (!userId) return;

      try {
        setUserRateRequests([]);

        const rateReqRef = dbRef(database, `rateRequest/${userId}`);
        const snapshot = await get(rateReqRef);

        if (!snapshot.exists()) return;

        const data = snapshot.val();
        const requests = [];

        Object.values(data).forEach((node) => {
          // CASE 1: Direct request object
          if (node.brand && node.timestamp) {
            requests.push({
              brand: node.brand || "",
              siteAddress: node.siteAddress || {},
              timestamp: node.timestamp || "",
              status: node.status || "",
            });
          }
          // CASE 2: Grouped requests (officeId → requestId)
          else if (typeof node === "object") {
            Object.values(node).forEach((req) => {
              if (!req || !req.timestamp) return;
              requests.push({
                brand: req.brand || "",
                siteAddress: req.siteAddress || {},
                timestamp: req.timestamp || "",
                status: req.status || "",
              });
            });
          }
        });

        requests.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setUserRateRequests(requests);
      } catch (err) {
        console.error("Error fetching rate requests:", err);
      }
    },
    [database],
  );

  const fetchOrderHistory = useCallback(
    async (userId) => {
      if (!userId) return;

      try {
        setLoading(true);
        setOrderHistory([]);
        setPendingOrdersCount(0);

        const ordersRef = dbRef(database, `orders/${userId}`);
        const ordersSnapshot = await get(ordersRef);

        if (ordersSnapshot.exists()) {
          const ordersData = ordersSnapshot.val();
          const ordersArray = Object.entries(ordersData).map(
            ([orderId, orderData]) => ({
              id: orderId,
              ...orderData,
            }),
          );
          setOrderHistory(ordersArray);

          const pendingCount = ordersArray.filter(
            (order) => order.status?.toLowerCase() === "pending",
          ).length;
          setPendingOrdersCount(pendingCount);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching order history:", err);
        setError(`Error fetching order history: ${err.message}`);
        setLoading(false);
      }
    },
    [database],
  );

  // ==========================================
  // EFFECTS
  // ==========================================

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const usersRef = dbRef(database, "profileDashboard");
        onValue(usersRef, (snapshot) => {
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const usersArray = Object.keys(usersData)
              .map((uid) => ({
                uid,
                ...usersData[uid],
              }))
              // 🔥 ONLY MAIN USERS
              .filter((user) => user.type === "main" || user.type === "firm")

            setUsers(usersArray);
          } else {
            setUsers([]);
          }
          setLoading(false);
        });
      } catch (err) {
        setError(`Error fetching users: ${err.message}`);
        setLoading(false);
      }
    };

    fetchUsers();
  }, [database]);

  // Fetch depots
  useEffect(() => {
    const depotsRef = ref(db, "Cement-depos");
    onValue(depotsRef, (snapshot) => {
      const data = snapshot.val();
      const depotsList = [];
      for (const id in data) {
        depotsList.push({ id, ...data[id] });
      }
      setDepots(depotsList);
    });
  }, []);

  // Load copied addresses from localStorage
  useEffect(() => {
    const loadCopiedAddresses = () => {
      const addresses = JSON.parse(
        localStorage.getItem("copiedSiteAddresses") || "[]",
      );
      setCopiedAddresses(addresses);
    };

    loadCopiedAddresses();

    const handleAddressCopied = (event) => {
      loadCopiedAddresses();
      if (event.detail?.message) {
        alert(event.detail.message);
      }
    };

    window.addEventListener("siteAddressCopied", handleAddressCopied);

    return () => {
      window.removeEventListener("siteAddressCopied", handleAddressCopied);
    };
  }, []);

  // Fetch data when user is selected
  useEffect(() => {
    if (selectedUser) {
      // Reset states
      setUserCodes([]);
      setExpandedCodes({});
      setUserDocuments({});
      setMtcFiles([]);
      setStatementFiles([]);
      setEditingCode(null);
      setEditedCodeData(null);
      setOrderHistory([]);
      setPendingOrdersCount(0);

      // Fetch user data
      fetchUserDocuments(selectedUser.uid);
      fetchUserFiles(selectedUser.uid);
      fetchUserCodes(selectedUser.uid);
      fetchOrderHistory(selectedUser.uid);
      fetchUserRateRequests(selectedUser.uid);
    }
  }, [
    selectedUser,
    fetchUserDocuments,
    fetchUserFiles,
    fetchUserCodes,
    fetchUserRateRequests,
    fetchOrderHistory,
  ]);

  // Missing documents notification
  useEffect(() => {
    const today = new Date();
    const lastDay = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
    ).getDate();

    if (!selectedUser) return;

    if (today.getDate() === lastDay) {
      const { globalMissing, codeWiseMissing } = getMissingDocuments(
        userDocuments,
        mtcFiles,
        statementFiles,
      );

      if (globalMissing.length > 0) {
        sendMissingDocNotification(selectedUser, globalMissing, "global");
      }

      Object.entries(codeWiseMissing).forEach(([codeId, docs]) => {
        sendMissingDocNotification(selectedUser, docs, codeId);
      });
    }
  }, [userDocuments, mtcFiles, statementFiles, selectedUser]);

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  const handleUserSelect = (user) => {
    setUserCodes([]);
    setExpandedCodes({});
    setSelectedUser(user);
    setActiveTab("profile");
    setError("");
    setOrderHistory([]);
    setPendingOrdersCount(0);
    setNewCodeForm(INITIAL_CODE_FORM);
    setEditingCode(null);
    setEditedCodeData(null);
    setAddingSiteToCode(null);
  };

  const handleFileUpload = (e, fileType, customFileName = null) => {
    const file = e.target.files[0];
    if (!file || !selectedUser) return;

    // Size & type validation (unchanged)
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must not exceed 5MB!");
      return;
    }

    const validTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setError("Only PDF, JPEG, and PNG files are allowed.");
      return;
    }

    // ── Use custom name if provided, otherwise fallback to original name + timestamp
    let finalFileName;
    if (customFileName) {
      finalFileName = customFileName; // ← This is what the user entered!
    } else {
      // Fallback (only used if no custom name was passed)
      const timestamp = new Date().getTime();
      finalFileName = `${fileType}_${timestamp}_${file.name}`;
    }

    // ── Build storage path with the final filename
    let path = "";
    if (fileType === "mtc") {
      path = `mtc/${selectedUser.uid}/${finalFileName}`;
    } else if (fileType === "statement") {
      path = `user-statements/${selectedUser.uid}/${finalFileName}`;
    } else if (fileType === "formats") {
      path = `formats/${selectedUser.uid}/${finalFileName}`;
    } else if (fileType === "invoices") {
      path = `invoices/${selectedUser.uid}/${finalFileName}`;
    }

    const fileRef = storageRef(storage, path);

    const uploadTask = uploadBytesResumable(fileRef, file);

    setUploadProgress((prev) => ({ ...prev, [fileType]: 0 }));

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        setUploadProgress((prev) => ({ ...prev, [fileType]: progress }));
      },
      (error) => {
        setError(`Upload failed: ${error.message}`);
        setUploadProgress((prev) => ({ ...prev, [fileType]: null }));
      },
      async () => {
        setUploadProgress((prev) => ({ ...prev, [fileType]: 100 }));

        // ── Save to database with the SAME name the user chose
        try {
          const downloadURL = await getDownloadURL(fileRef);

          const db = getDatabase();
          const filesRef = ref(
            db,
            `users/${selectedUser.uid}/files/${fileType}`,
          );
          const newFileRef = push(filesRef);

          // ✅ Save file metadata
          await set(newFileRef, {
            name: finalFileName,
            url: downloadURL,
            uploadDate: new Date().toISOString(),
            size: file.size,
            type: file.type,
          });

          // ✅ SEND NOTIFICATION
          const notificationRef = ref(
            db,
            `user_notifications/Document_Uploaded/${selectedUser.uid}/${newFileRef.key}`,
          );

          await set(notificationRef, {
            title: "Document Uploaded",
            message: `${fileType.toUpperCase()} document "${finalFileName}" uploaded. Kindly check your downloads.`,
            gifUrl: GIF_URL,
            timestamp: getIndianTimestamp(),
            seen: false,
          });

          // ✅ Refresh UI
          fetchUserFiles(selectedUser.uid);

          setTimeout(() => {
            setUploadProgress((prev) => ({ ...prev, [fileType]: null }));
          }, 3000);
        } catch (err) {
          setError(`Upload process failed: ${err.message}`);
        }
      },
    );
  };

  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      setError("File URL not found. Please try again.");
      return;
    }

    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Code handlers
  const toggleCodeExpansion = (codeId) => {
    setExpandedCodes((prev) => ({ ...prev, [codeId]: !prev[codeId] }));
  };

  const startEditingCode = (code) => {
    setEditingCode(code.id);
    setEditedCodeData({ ...code });
  };

  const cancelEditingCode = () => {
    setEditingCode(null);
    setEditedCodeData(null);
  };

  const handleEditedCodeChange = (field, value) => {
    if (!editedCodeData) return;

    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setEditedCodeData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setEditedCodeData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const saveEditedCode = async () => {
    if (!editedCodeData || !selectedUser) return;

    try {
      setLoading(true);
      const codeRef = dbRef(
        database,
        `new-code/${selectedUser.uid}/codes/${editingCode}`,
      );
      await set(codeRef, editedCodeData);

      setEditingCode(null);
      setEditedCodeData(null);
      fetchUserCodes(selectedUser.uid);
      alert("Code updated successfully!");
    } catch (err) {
      console.error("Error updating code:", err);
      setError(`Error updating code: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleAddingSite = (codeId) => {
    if (addingSiteToCode === codeId) {
      setAddingSiteToCode(null);
      setNewSiteForm({ address: "", state: "", pincode: "" });
    } else {
      setAddingSiteToCode(codeId);
      setNewSiteForm({ address: "", state: "", pincode: "" });
    }
  };

  const handleNewSiteChange = (field, value) => {
    setNewSiteForm((prev) => ({ ...prev, [field]: value }));
  };

  const addNewSiteToCode = async (codeId) => {
    if (!newSiteForm.address || !newSiteForm.state) {
      setError("Address and state are required");
      return;
    }

    if (newSiteForm.pincode && !/^\d{6}$/.test(newSiteForm.pincode)) {
      setError("Pincode must be a 6-digit number");
      return;
    }

    try {
      setLoading(true);

      const codeRef = dbRef(
        database,
        `new-code/${selectedUser.uid}/codes/${codeId}`,
      );
      const codeSnapshot = await get(codeRef);

      if (codeSnapshot.exists()) {
        const codeData = codeSnapshot.val();
        const sites = codeData.sites || {};
        const siteNumbers = Object.keys(sites).map(Number);
        const nextSiteNumber =
          siteNumbers.length > 0 ? Math.max(...siteNumbers) + 1 : 1;

        const updatedSites = { ...sites, [nextSiteNumber]: newSiteForm };

        await set(
          dbRef(database, `new-code/${selectedUser.uid}/codes/${codeId}/sites`),
          updatedSites,
        );

        setNewSiteForm({ address: "", state: "", pincode: "" });
        setAddingSiteToCode(null);
        fetchUserCodes(selectedUser.uid);
        alert("New site added successfully!");
      } else {
        setError("Code not found");
      }
    } catch (err) {
      console.error("Error adding new site:", err);
      setError(`Error adding new site: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewCodeChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setNewCodeForm((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setNewCodeForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleNewCodeSiteChange = (field, value) => {
    setNewCodeForm((prev) => ({
      ...prev,
      sites: { ...prev.sites, 1: { ...prev.sites[1], [field]: value } },
    }));
  };

  const handleSubmitNewCode = async () => {
    const { isValid } = validateCodeForm(newCodeForm, depots);

    if (!isValid || !selectedUser) {
      return;
    }

    try {
      setLoading(true);

      const sanitizedCodeData = {
        codeID: newCodeForm.codeID?.trim() || "",
        brand: newCodeForm.brand?.trim() || "",
        depoName: newCodeForm.depoName?.trim() || "",
        balance: newCodeForm.balance || "",
        depoAccount: {
          AccountNumber: newCodeForm.depoAccount?.AccountNumber?.trim() || "",
          BeneficiaryName:
            newCodeForm.depoAccount?.BeneficiaryName?.trim() || "",
          IFSC: newCodeForm.depoAccount?.IFSC?.trim() || "",
          Branch: newCodeForm.depoAccount?.Branch?.trim() || "",
        },
        sites: newCodeForm.sites || {},
      };

      const userCodesRef = dbRef(
        database,
        `new-code/${selectedUser.uid}/codes`,
      );
      const newCodeRef = push(userCodesRef);
      const newCodeKey = newCodeRef.key;

      await set(
        dbRef(database, `new-code/${selectedUser.uid}/codes/${newCodeKey}`),
        sanitizedCodeData,
      );

      const siteAddress = newCodeForm.sites[1]?.address || "N/A";
      const notificationTitle = "New Code Added";
      const notificationMessage = `A new code has been added under ${newCodeForm.brand}
Code ID: ${newCodeForm.codeID}
Site Address: ${siteAddress}
You can now proceed with your cement order.`;

      await sendNotification(
        selectedUser.uid,
        newCodeKey,
        notificationTitle,
        notificationMessage,
      );

      setNewCodeForm(INITIAL_CODE_FORM);
      setValidation(INITIAL_VALIDATION);
      fetchUserCodes(selectedUser.uid);
      alert("New code added successfully!");
    } catch (err) {
      console.error("Error submitting new code:", err);
      setError(`Error submitting new code: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteCode = async (codeId) => {
    if (!selectedUser || !codeId) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this code? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const codeRef = dbRef(
        database,
        `new-code/${selectedUser.uid}/codes/${codeId}`,
      );
      await remove(codeRef);
      fetchUserCodes(selectedUser.uid);
      alert("Code deleted successfully!");
    } catch (err) {
      console.error("Error deleting code:", err);
      setError(`Error deleting code: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Address handlers
  const applyCopiedAddress = (addressData) => {
    setNewCodeForm((prev) => ({
      ...prev,
      sites: {
        ...prev.sites,
        1: {
          address: addressData.address,
          state: addressData.state,
          pincode: addressData.pincode,
        },
      },
    }));
    setShowAddressDropdown(false);
    setValidation((prev) => ({
      ...prev,
      sites: { isValid: true, message: "" },
    }));
  };

  const applyCopiedAddressForNewSite = (addressData) => {
    setNewSiteForm({
      address: addressData.address,
      state: addressData.state,
      pincode: addressData.pincode,
    });
    setShowNewSiteAddressDropdown(false);
  };

  const clearCopiedAddress = (index) => {
    const updatedAddresses = copiedAddresses.filter((_, i) => i !== index);
    localStorage.setItem(
      "copiedSiteAddresses",
      JSON.stringify(updatedAddresses),
    );
    setCopiedAddresses(updatedAddresses);
  };

  const clearAllCopiedAddresses = () => {
    if (
      window.confirm("Are you sure you want to clear all copied addresses?")
    ) {
      localStorage.removeItem("copiedSiteAddresses");
      setCopiedAddresses([]);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    const searchFields = [
      user.username,
      user.email,
      user.company_name,
      user.phone,
      user.gstin,
      user.pan,
    ];
    return searchFields.some(
      (field) =>
        field && field.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  });

  // ==========================================
  // RENDER
  // ==========================================

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileSection selectedUser={selectedUser} />;
      case "uploads":
        return (
          <UploadsSection
            selectedUser={selectedUser}
            mtcFiles={mtcFiles}
            statementFiles={statementFiles}
            formatsFiles={formatsFiles}
            invoicesFiles={invoicesFiles}
            uploadProgress={uploadProgress}
            handleFileUpload={handleFileUpload}
            handleDownload={handleDownload}
            onDeleteFile={handleDeleteFile} // ✅ ADD
            onDeleteAll={handleDeleteAllFiles} // ✅ ADD
          />
        );
      case "documents":
        return (
          <DocumentsSection
            selectedUser={selectedUser}
            docsLoading={docsLoading}
            userDocuments={userDocuments}
            sendMissingDocNotification={sendMissingDocNotification}
          />
        );
      case "codeDetails":
        return (
          <CodeDetailsSection
            selectedUser={selectedUser}
            loading={loading}
            userCodes={userCodes}
            expandedCodes={expandedCodes}
            editingCode={editingCode}
            editedCodeData={editedCodeData}
            addingSiteToCode={addingSiteToCode}
            newSiteForm={newSiteForm}
            newCodeForm={newCodeForm}
            validation={validation}
            rateRequests={userRateRequests}
            requestsLoading={false} // or a new state if you want a loading indicator
            depots={depots}
            copiedAddresses={copiedAddresses}
            showAddressDropdown={showAddressDropdown}
            showNewSiteAddressDropdown={showNewSiteAddressDropdown}
            toggleCodeExpansion={toggleCodeExpansion}
            startEditingCode={startEditingCode}
            cancelEditingCode={cancelEditingCode}
            handleEditedCodeChange={handleEditedCodeChange}
            saveEditedCode={saveEditedCode}
            toggleAddingSite={toggleAddingSite}
            handleNewSiteChange={handleNewSiteChange}
            addNewSiteToCode={addNewSiteToCode}
            handleNewCodeChange={handleNewCodeChange}
            handleNewCodeSiteChange={handleNewCodeSiteChange}
            handleSubmitNewCode={handleSubmitNewCode}
            deleteCode={deleteCode}
            setAddingSiteToCode={setAddingSiteToCode}
            setShowAddressDropdown={setShowAddressDropdown}
            setShowNewSiteAddressDropdown={setShowNewSiteAddressDropdown}
            applyCopiedAddress={applyCopiedAddress}
            applyCopiedAddressForNewSite={applyCopiedAddressForNewSite}
            clearCopiedAddress={clearCopiedAddress}
            clearAllCopiedAddresses={clearAllCopiedAddresses}
          />
        );

      case "manualOrder":
        return (
          <ManualOrderSection
            selectedUser={selectedUser}
            userCodes={userCodes} // same prop used by CodeDetailsSection
            codes={userCodes} // fallback alias (if page expects `codes`)
            depots={depots}
            loading={loading}
            copiedAddresses={copiedAddresses}
          />
        );

      // 👉 ADD THIS HERE
      case "purchaseOrders":
        return <PurchaseOrderSection selectedUser={selectedUser} />;
      case "orderHistory":
        return (
          <OrderHistorySection
            selectedUser={selectedUser}
            loading={loading}
            orderHistory={orderHistory}
            pendingOrdersCount={pendingOrdersCount}
          />
        );
      default:
        return null;
    }
  };

  const handleDeleteFile = async (categoryKey, fileName) => {
    if (!selectedUser || !categoryKey || !fileName) return;

    try {
      let path = "";

      if (categoryKey === "mtc") {
        path = `mtc/${selectedUser.uid}/${fileName}`;
      } else if (categoryKey === "statement") {
        path = `user-statements/${selectedUser.uid}/${fileName}`;
      } else if (categoryKey === "formats") {
        path = `formats/${selectedUser.uid}/${fileName}`;
      } else if (categoryKey === "invoices") {
        path = `invoices/${selectedUser.uid}/${fileName}`;
      }

      const fileRef = storageRef(storage, path);
      await deleteObject(fileRef);

      // Refresh list
      fetchUserFiles(selectedUser.uid);
    } catch (err) {
      console.error("Delete file error:", err);
      setError(`Failed to delete file: ${err.message}`);
    }
  };

  const handleDeleteAllFiles = async (categoryKey) => {
    if (!selectedUser || !categoryKey) return;

    try {
      let path = "";

      if (categoryKey === "mtc") path = `mtc/${selectedUser.uid}`;
      if (categoryKey === "statement")
        path = `user-statements/${selectedUser.uid}`;
      if (categoryKey === "formats") path = `formats/${selectedUser.uid}`;
      if (categoryKey === "invoices") path = `invoices/${selectedUser.uid}`;

      const dirRef = storageRef(storage, path);
      const list = await listAll(dirRef);

      await Promise.all(list.items.map((item) => deleteObject(item)));

      fetchUserFiles(selectedUser.uid);
    } catch (err) {
      console.error("Delete all files error:", err);
      setError(`Failed to delete all files: ${err.message}`);
    }
  };

  return (
    <div className="client-records">
      <div className="management-container">
        {/* Users Sidebar */}
        <aside className="users-sidebar">
          <div className="sidebar-header">
            <h3>
              <FaUser /> Clients
            </h3>
          </div>
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                maxLength={37}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="users-list">
            {loading && users.length === 0 ? (
              <div className="loading-state">
                <FaSpinner className="spinner" /> Loading users...
              </div>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div
                  key={user.uid}
                  className={`user-item ${selectedUser?.uid === user.uid ? "selected" : ""}`}
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="user-avatar">
                    <FaUser />
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {user.username || "Unnamed User"}
                    </div>
                    <div className="user-company">{user.company_name}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">No users found</div>
            )}
          </div>
        </aside>

        {/* Content Area */}
        <main className="content-area">
          {selectedUser ? (
            <>
              {/* Tab Navigation */}
              <nav className="tabs-navigation">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    className={`tab-button ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                  >
                    <Icon />
                    <span>{label}</span>
                    {id === "orderHistory" && pendingOrdersCount > 0 && (
                      <span className="badge">{pendingOrdersCount}</span>
                    )}
                  </button>
                ))}
              </nav>

              {/* Tab Content */}
              <div className="tab-content">{renderTabContent()}</div>
            </>
          ) : (
            <div className="no-user-selected">
              <div className="no-user-content">
                <FaUser className="no-user-icon" />
                <h2>No User Selected</h2>
                <p>
                  Please select a user from the list to view their profile and
                  manage documents.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-toast">
          <p>{error}</p>
          <button onClick={() => setError("")} className="close-error">
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientRecords;
